import { useCallback, useEffect, useState } from "react";
import type { F01StartContext } from "@care-by-ella/contracts";
import { F01_ORIGINALS_ANCHOR } from "@/lib/coach-brief-anchors";
import { coachDesiredSupport } from "@/lib/coach-desired-support";
import {
  fetchF01StartContext,
  ForbiddenError,
  UnauthorizedError,
} from "@/lib/clinician-api";

// TES-818 point 1 (A-01, Emelie 2026-09-20) — F-01 as the coach's persisting
// start context, with the original answers reachable where the coach has the
// right to them.
//
// TES-1074 point 2 — demoted to the SOURCE. Emelie:
// "Den nuvarande rutan Sammanfattning av hälsodeklarationen visar huvudsakligen patientens generella F‑01-återkoppling och uppfyller inte detta krav."
// "Patienten i korthet" is now the overview, and F-01's facts already reach
// it through Patientminnet, so the generic reflection that used to head this
// box was a second standalone display of the same answers (point 3). What is
// left is what nothing else shows: her original answers, question by
// question. The heading also stops calling F-01 the health declaration, which
// is the medical intake, a different form.
//
// A RESTRICTED ROW IS STILL A ROW. The server sends doctor-only questions
// with `values: []`, and that emptiness is the neutral status A-01 asks for:
// the coach sees THAT the patient answered, never what.
//
// The answers open when the page's hash is the anchor "Patienten i korthet"
// links to, so the link lands on the answers rather than on a closed toggle.
export function F01StartContextPanel({
  patientId,
  onUnauthorized,
}: {
  patientId: string;
  onUnauthorized?: () => void;
}) {
  const [context, setContext] = useState<F01StartContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);

  const load = useCallback(async () => {
    try {
      setContext(await fetchF01StartContext(patientId));
      setError(null);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized?.();
        return;
      }
      // 403 is an ordinary fact about who is looking, not a failure: an
      // employee this panel was never meant for. Hide, do not shout.
      if (e instanceof ForbiddenError) {
        setForbidden(true);
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [patientId, onUnauthorized]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const open = () => {
      if (window.location.hash === `#${F01_ORIGINALS_ANCHOR}`) {
        setShowAnswers(true);
      }
    };
    open();
    window.addEventListener("hashchange", open);
    return () => window.removeEventListener("hashchange", open);
  }, []);

  if (forbidden) return null;

  // TES-1321 — "Önskat stöd" as its own named block above the originals.
  // Background context only: it opens nothing, assigns nothing, notifies
  // nobody. Absent for F-01 v10 and earlier, which never asked.
  const desiredSupport = coachDesiredSupport(context);

  return (
    <>
      {desiredSupport ? (
        <section
          aria-labelledby="f01-desired-support-heading"
          className="rounded-md border border-surfaceMuted bg-surface p-md"
        >
          <h3
            id="f01-desired-support-heading"
            className="mb-xs font-display text-lg"
          >
            Önskat stöd
          </h3>
          <ul className="mb-sm flex flex-col gap-xs text-sm text-textPrimary">
            {desiredSupport.labels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
          <p className="text-xs text-textSecondary">
            {desiredSupport.source}
            {desiredSupport.submitted ? ` · ${desiredSupport.submitted}` : ""}
            {" · "}
            <a
              href={`#${F01_ORIGINALS_ANCHOR}`}
              onClick={() => setShowAnswers(true)}
              className="text-brand hover:underline"
            >
              Öppna originalsvaret
            </a>
          </p>
        </section>
      ) : null}
      <section
        id={F01_ORIGINALS_ANCHOR}
        className="scroll-mt-48 rounded-md border border-surfaceMuted bg-surface p-md"
      >
        <h3 className="mb-sm font-display text-lg">
          Originalsvar – Första frågorna (F-01)
        </h3>

        {error ? (
          <p className="text-sm text-danger">Kunde inte hämta: {error}</p>
        ) : !context ? (
          <p className="text-sm text-textSecondary">Hämtar…</p>
        ) : context.status === "not_started" ? (
          <p className="text-sm text-textSecondary">
            Patienten har inte besvarat Första frågorna än.
          </p>
        ) : (
          <div className="flex flex-col gap-sm">
            <p className="text-xs text-textSecondary">
              Besvarad {context.submittedAt?.slice(0, 10) ?? "–"}
              {context.source === "lead" ? " — före kontoregistreringen" : ""}
            </p>

            {context.answers.length > 0 ? (
              <div>
                <button
                  type="button"
                  onClick={() => setShowAnswers((v) => !v)}
                  className="text-sm text-brand hover:underline"
                  aria-expanded={showAnswers}
                >
                  {showAnswers ? "Dölj originalsvaren" : "Visa originalsvaren"}
                </button>
                {showAnswers ? (
                  <dl className="mt-sm flex flex-col gap-sm">
                    {context.answers.map((a) => (
                      <div key={a.key}>
                        <dt className="text-xs text-textSecondary">
                          {a.label}
                        </dt>
                        <dd className="text-sm text-textPrimary">
                          {a.access === "restricted" ? (
                            <span className="text-textSecondary">
                              Besvarad — visas för läkaren
                            </span>
                          ) : (
                            a.values.join(" · ") || "—"
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </section>
    </>
  );
}
